// Active-branch selection for the org admin.
//
// When an org admin logs in with branch_avail=true, they pick a branch on the
// BranchSelect page; that choice is stored here and sent as ?branch= on
// branch-scoped list APIs (e.g. the employee list). A branch admin / employee
// never uses this — their data is already server-scoped to their own branch.
//
// TWO independent pieces of state:
//   * MODE  (active_branch_mode): 'overview' | 'branch'  — what the org admin
//     entered from the selection page. In 'overview' the sidebar/reports are the
//     restricted org-wide view. Picking a specific branch switches to 'branch'
//     and restores the full permission-based menu.
//   * FILTER (active_branch_id): 'all' | <branch id> — which branch's data is
//     currently shown. In overview this is 'all'; in branch mode it is the
//     selected branch id.
const ACTIVE_BRANCH_ID = "active_branch_id";
const ACTIVE_BRANCH_NAME = "active_branch_name";
const ACTIVE_BRANCH_MAIN = "active_branch_main";
const ACTIVE_BRANCH_MODE = "active_branch_mode";

// Event fired when mode or branch filter changes, so the sidebar and
// branch-scoped pages re-read sessionStorage without a full reload.
export const BRANCH_FILTER_CHANGED_EVENT = "hrms:branch-filter-changed";

const dispatchBranchSessionChanged = () => {
    if (typeof window !== "undefined") {
        window.dispatchEvent(new Event(BRANCH_FILTER_CHANGED_EVENT));
    }
};

// --- entering a specific branch (full app, branch-scoped) ---
export const setActiveBranch = (id, name = "", isMain = false) => {
    if (id === null || id === undefined || id === "") {
        clearActiveBranch();
        return;
    }
    sessionStorage.setItem(ACTIVE_BRANCH_MODE, "branch");
    sessionStorage.setItem(ACTIVE_BRANCH_ID, String(id));
    sessionStorage.setItem(ACTIVE_BRANCH_NAME, name || "");
    sessionStorage.setItem(ACTIVE_BRANCH_MAIN, isMain ? "true" : "false");
    window.dispatchEvent(new Event(BRANCH_FILTER_CHANGED_EVENT));
};

// --- entering org-wide Dashboard Overview mode ---
export const setOverviewMode = (filterId = "all", filterName = "") => {
    sessionStorage.setItem(ACTIVE_BRANCH_MODE, "overview");
    sessionStorage.setItem(ACTIVE_BRANCH_ID, String(filterId));
    sessionStorage.setItem(ACTIVE_BRANCH_NAME, filterName || "");
    sessionStorage.setItem(ACTIVE_BRANCH_MAIN, "false");
    dispatchBranchSessionChanged();
};

export const getActiveBranchId = () => {
    const v = sessionStorage.getItem(ACTIVE_BRANCH_ID);
    return v && v !== "null" && v !== "" ? v : null;
};

export const getActiveBranchName = () =>
    sessionStorage.getItem(ACTIVE_BRANCH_NAME) || "";

// Whether the currently active branch is the organisation's main branch.
export const getActiveBranchIsMain = () =>
    sessionStorage.getItem(ACTIVE_BRANCH_MAIN) === "true";

export const clearActiveBranch = () => {
    const hadState =
        sessionStorage.getItem(ACTIVE_BRANCH_MODE) != null ||
        sessionStorage.getItem(ACTIVE_BRANCH_ID) != null;
    sessionStorage.removeItem(ACTIVE_BRANCH_ID);
    sessionStorage.removeItem(ACTIVE_BRANCH_NAME);
    sessionStorage.removeItem(ACTIVE_BRANCH_MAIN);
    sessionStorage.removeItem(ACTIVE_BRANCH_MODE);
    if (hadState) {
        dispatchBranchSessionChanged();
    }
};

// True when the logged-in user is an org admin who has branches to choose from.
// For org admins this is derived from multi_branch + org_admin_branch_selection so
// stale branch_avail from an old login cannot keep overview / header UI visible.
export const isBranchAvail = () => {
    if (!isOrgAdminUser()) {
        return sessionStorage.getItem("branch_avail") === "true";
    }
    const multiBranch = sessionStorage.getItem("multi_branch") === "true";
    return multiBranch && isOrgAdminBranchSelectionEnabled();
};

/**
 * Align branch_avail and active-branch session with org_admin_branch_selection.
 * Call on login restore, route changes, and when company config is saved.
 */
export function enforceOrgAdminBranchPolicy() {
    if (!isOrgAdminUser()) return;

    const multiBranch = sessionStorage.getItem("multi_branch") === "true";
    const selectionEnabled = isOrgAdminBranchSelectionEnabled();
    const branchAvail = String(multiBranch && selectionEnabled);

    sessionStorage.setItem("branch_avail", branchAvail);
    sessionStorage.setItem("org_admin_branch_selection", String(selectionEnabled));

    if (localStorage.getItem("keepSignedIn")) {
        localStorage.setItem("branch_avail", branchAvail);
        localStorage.setItem("org_admin_branch_selection", String(selectionEnabled));
    }

    if (!selectionEnabled) {
        clearActiveBranch();
        return;
    }

    // Older builds stored a named branch while staying in overview mode
    // (header said Bangalore, sidebar stayed on the 4-item overview menu).
    // Promote that to branch mode so the full sidebar matches the header.
    const mode = sessionStorage.getItem(ACTIVE_BRANCH_MODE);
    const id = sessionStorage.getItem(ACTIVE_BRANCH_ID);
    if (mode === "overview" && id && id !== "all") {
        sessionStorage.setItem(ACTIVE_BRANCH_MODE, "branch");
        dispatchBranchSessionChanged();
    }
}

export const getSessionBranchId = () => {
    const value = sessionStorage.getItem("branch_id");
    return value && value !== "null" && value !== "" ? String(value) : null;
};

export const getSessionBranchName = () =>
    sessionStorage.getItem("branch_name") || "";

/** Org admin (not branch admin). */
export const isOrgAdminUser = () => {
    const isAdmin =
        sessionStorage.getItem("is_admin") === "true" ||
        sessionStorage.getItem("is_super_admin") === "true";
    const isBranchAdmin = sessionStorage.getItem("is_branch_admin") === "true";
    return isAdmin && !isBranchAdmin;
};

/** CustomConfiguration.org_admin_branch_selection (default true). */
export const isOrgAdminBranchSelectionEnabled = () => {
    const cfg = JSON.parse(sessionStorage.getItem("customConfig") || "{}");
    if (typeof cfg.org_admin_branch_selection === "boolean") {
        return cfg.org_admin_branch_selection;
    }
    if (sessionStorage.getItem("org_admin_branch_selection") != null) {
        return sessionStorage.getItem("org_admin_branch_selection") === "true";
    }
    return true;
};

/** True when org-admin branch picker / header control should be shown. */
export const shouldShowOrgAdminBranchUI = () =>
    isOrgAdminBranchSelectionEnabled() && isBranchAvail();

/** Org admin entered a specific branch from the /branches picker (not overview). */
export const isOrgAdminInBranchMode = () =>
    isBranchAvail() && sessionStorage.getItem(ACTIVE_BRANCH_MODE) === "branch";

/**
 * When org_admin_branch_selection is on, org admins who picked a branch should
 * not see Settings > Configuration (overview mode still may).
 */
export const shouldHideConfigurationForOrgAdmin = () =>
    isOrgAdminUser() &&
    isOrgAdminBranchSelectionEnabled() &&
    isOrgAdminInBranchMode();

// Boolean key: is this an ORG ADMIN currently in org-wide "Dashboard Overview"
// mode? Driven by the MODE flag. Picking a specific branch from the header or
// branch scorecard switches to branch mode and clears this.
export const isOrgOverview = () =>
    isOrgAdminBranchSelectionEnabled() &&
    isBranchAvail() &&
    sessionStorage.getItem(ACTIVE_BRANCH_MODE) === "overview";

// The branch the overview is currently filtered to: null means "all branches"
// (no filter). A concrete id scopes overview data to that branch.
export const getOverviewBranchFilter = () => {
    const v = getActiveBranchId();
    return v && v !== "all" ? v : null;
};

/** Stable key for React effects when the header branch filter changes. */
export const getListBranchScopeKey = () => {
    const q = getListBranchQuery();
    return q.branch ? String(q.branch) : "all";
};

/**
 * ?branch= query for org-admin list/dashboard APIs when the header dropdown
 * (or branch picker) has a specific branch selected. Empty when "All Branches".
 */
export const getListBranchQuery = () => {
    if (!isOrgAdminUser()) return {};
    const overviewFilter = getOverviewBranchFilter();
    if (overviewFilter) return { branch: overviewFilter };
    const active = getActiveBranchId();
    if (active && active !== "all") return { branch: active };
    return {};
};

/**
 * ?branch= for dropdown APIs — org admins use the header/picker branch;
 * branch admins and employees use their assigned session branch.
 */
export const getDropdownBranchQuery = () => {
    if (!isOrgAdminUser()) {
        const sessionBranch = getSessionBranchId();
        return sessionBranch ? { branch: sessionBranch } : {};
    }
    return getListBranchQuery();
};

export function syncMultiBranchFlag(multiBranch) {
    const value = String(Boolean(multiBranch));
    sessionStorage.setItem("multi_branch", value);
    if (localStorage.getItem("keepSignedIn")) {
        localStorage.setItem("multi_branch", value);
    }
    enforceOrgAdminBranchPolicy();
}

const BRANCH_MARKER_RE = /^__branch:(\d+)__(?:\|(.*))?$/s;

/** Split encoded candidate/intern work_location into branch id + human-readable location. */
export function decodeCandidateBranchWorkLocation(workLocation) {
    if (workLocation == null || workLocation === "") {
        return { branchId: null, location: null };
    }
    const raw = String(workLocation);
    const match = raw.match(BRANCH_MARKER_RE);
    if (match) {
        return {
            branchId: Number(match[1]),
            location: match[2] || null,
        };
    }
    return { branchId: null, location: raw };
}

/** Return only the displayable work location (never the internal branch marker). */
export function stripBranchMarkerFromWorkLocation(workLocation) {
    return decodeCandidateBranchWorkLocation(workLocation).location;
}

// Kept for callers that still want overview + an optional filter.
// Named-branch picks from the header now use setActiveBranch instead.
export const switchOverviewBranch = (filterId = "all", filterName = "") => {
    setOverviewMode(filterId, filterName);
};
